// Precondition: None
// Postcondition: Custom element class for a modal dialog using native <dialog> element

import { icon } from '../../../shared/icons.js';

class FtDialog extends HTMLElement {
  private dialogElement: HTMLDialogElement | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes() {
    return ["open", "no-header"];
  }

  connectedCallback(): void {
    this.render();
    this.setupEventListeners();
    // Apply no-header attribute if present at connect time
    if (this.hasAttribute("no-header")) {
      const header = this.shadowRoot?.querySelector<HTMLElement>(".header");
      if (header) header.hidden = true;
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (name === "open" && oldValue !== newValue && this.dialogElement) {
      if (this.hasAttribute("open")) {
        this.open();
      } else {
        this.close();
      }
    }
    if (name === "no-header" && this.shadowRoot) {
      const header = this.shadowRoot.querySelector<HTMLElement>(".header");
      if (header) {
        header.hidden = this.hasAttribute("no-header");
      }
    }
  }

  private render(): void {
    const style = document.createElement("style");
    style.textContent = this.getStyles();

    const template = document.createElement("template");
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = "";
      this.shadowRoot.appendChild(style);
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  private getStyles(): string {
    return `
      :host {
        --dialog-bg: var(--bg, #ffffff);
        --dialog-text: var(--text, #1c1c1e);
        --dialog-border: var(--input-border, #e2e4e8);
        --dialog-close-hover: var(--tab-bg, #f4f4f5);
      }

      @media (prefers-color-scheme: dark) {
        :host {
          --dialog-bg: var(--bg, #1f2937);
          --dialog-text: var(--text, #f3f4f6);
          --dialog-border: var(--input-border, #272a31);
          --dialog-close-hover: var(--tab-bg, #111216);
        }
      }

      dialog {
        padding: 0;
        border: 1px solid var(--dialog-border);
        border-radius: var(--radius-md, 12px);
        background: var(--dialog-bg);
        color: var(--dialog-text);
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        max-width: 600px;
        max-height: 85dvh;
        min-height: 200px;
        width: 100%;
      }

      dialog[open] {
        display: flex;
        flex-direction: column;
      }

      dialog::backdrop {
        background: rgba(0, 0, 0, 0.5);
      }

      @media (prefers-reduced-motion: no-preference) {
        dialog::backdrop {
          animation: fade-in 200ms ease-out;
        }

        dialog[open] {
          animation: slide-up 300ms ease-out;
        }
      }

      @keyframes fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes slide-up {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* Mobile: Full-width with minimal margins */
      @media (max-width: 479px) {
        dialog {
          max-width: 100%;
          max-height: 100dvh;
          border-radius: var(--radius, 8px);
          margin: 0;
        }
      }

      /* Tablet: Wider dialog */
      @media (min-width: 480px) and (max-width: 767px) {
        dialog {
          max-width: 90%;
          max-height: 90dvh;
        }
      }

      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        border-bottom: 1px solid var(--dialog-border);
        flex-shrink: 0;
        padding: 1.5rem;
      }

      .header[hidden] {
        display: none;
      }

      @media (max-width: 479px) {
        .header {
          padding: 1rem;
        }
      }

      .title {
        margin: 0;
        font-weight: 600;
        color: var(--dialog-text);
        font-family: "Space Grotesk", system-ui, sans-serif;
        font-size: 18px;
        line-height: 1.2;
        word-break: break-word;
      }

      @media (max-width: 479px) {
        .title {
          font-size: 16px;
        }
      }

      .close-button {
        border: none;
        background: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--dialog-text);
        width: 36px;
        height: 36px;
        border-radius: var(--radius-sm, 6px);
        flex-shrink: 0;
        -webkit-tap-highlight-color: transparent;
        user-select: none;
      }

      @media (max-width: 479px) {
        .close-button {
          width: 44px;
          height: 44px;
          border-radius: var(--radius, 8px);
        }
      }

      @media (prefers-reduced-motion: no-preference) {
        .close-button {
          transition: background-color 150ms ease;
        }

        .close-icon {
          transition: transform 150ms ease;
        }

        .close-button:hover .close-icon {
          transform: rotate(90deg);
        }
      }

      .close-button:hover {
        background: var(--dialog-close-hover);
      }

      .close-button:active {
        opacity: 0.8;
      }

      .close-icon {
        display: flex;
        width: 20px;
        height: 20px;
      }

      @media (max-width: 479px) {
        .close-icon {
          width: 24px;
          height: 24px;
        }
      }

      .content {
        flex: 1;
        overflow-y: auto;
        padding: 1.5rem;
        color: var(--dialog-text);
      }

      @media (max-width: 479px) {
        .content {
          padding: 1rem;
        }
      }

      .content::-webkit-scrollbar {
        width: 8px;
      }

      .content::-webkit-scrollbar-track {
        background: transparent;
      }

      .content::-webkit-scrollbar-thumb {
        background: var(--dialog-border);
        border-radius: var(--radius-xs, 4px);
      }

      .content::-webkit-scrollbar-thumb:hover {
        background: var(--dialog-text);
        opacity: 0.5;
      }
    `;
  }

  private getTemplate(): string {
    return `
      <dialog>
        <div class="header">
          <h2 class="title"></h2>
          <button class="close-button" aria-label="Close dialog">
            <span class="close-icon">${icon('x', 20)}</span>
          </button>
        </div>
        <div class="content">
          <slot></slot>
        </div>
      </dialog>
    `;
  }

  private setupEventListeners(): void {
    this.dialogElement = this.shadowRoot?.querySelector("dialog") as HTMLDialogElement;
    const closeButton = this.shadowRoot?.querySelector(".close-button") as HTMLButtonElement;

    closeButton?.addEventListener("click", () => this.close());

    // Handle backdrop click (click on dialog element but not its children)
    this.dialogElement?.addEventListener("click", (e) => {
      if (e.target === this.dialogElement) {
        this.close();
      }
    });

    // Handle native cancel event (Escape key)
    this.dialogElement?.addEventListener("cancel", (e) => {
      e.preventDefault();
      this.close();
    });

    // Handle data-close-dialog clicks from slotted content
    this.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.hasAttribute("data-close-dialog")) {
        this.close();
      }
    });
  }

  // Public API
  open(): void {
    if (this.dialogElement && !this.dialogElement.open) {
      this.dialogElement.showModal();
      this.setAttribute("open", "");
      this.focusFirstFormElement();
      this.dispatchEvent(
        new CustomEvent("opened", { bubbles: true, composed: true })
      );
    }
  }

  private focusFirstFormElement(): void {
    const focusable = this.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])'
    );
    focusable?.focus();
  }

  close(): void {
    if (this.dialogElement?.open) {
      this.dialogElement.close();
      this.removeAttribute("open");
      this.dispatchEvent(
        new CustomEvent("closed", { bubbles: true, composed: true })
      );
    }
  }

  toggle(): void {
    this.dialogElement?.open ? this.close() : this.open();
  }

  setTitle(title: string): void {
    const titleElement = this.shadowRoot?.querySelector(".title") as HTMLElement;
    if (titleElement) {
      titleElement.textContent = title;
    }
  }

  getTitle(): string {
    const titleElement = this.shadowRoot?.querySelector(".title") as HTMLElement;
    return titleElement?.textContent || "";
  }
}


customElements.define("ft-dialog", FtDialog);
